import React from 'react'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { APP_VERSION } from './app-version'
import { isUnactionableNetworkError } from './bugsnagFilter'

// Only initialize Bugsnag on the client side
if (typeof window !== 'undefined') {
  Bugsnag.start({
    apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY || 'e4f8dc8c681435171ed1a4c6f7e1570b',
    appType: 'client',
    appVersion: APP_VERSION,
    // NEXT_PUBLIC_RELEASE_STAGE is set per environment in the apphosting configs
    // (production / test). NODE_ENV is always 'production' in a Next.js build, so
    // it can't distinguish the test deploy from prod on its own.
    releaseStage: process.env.NEXT_PUBLIC_RELEASE_STAGE || process.env.NODE_ENV || 'development',
    // Deployed environments only. Local dev is deliberately excluded: its
    // stack frames point at Turbopack chunks no source map can resolve, its
    // errors are attributed to whatever commit happens to be checked out (a
    // dev server falling over was once blamed on a real release), and it
    // drags HMR and half-saved-file noise into the same stability score as
    // production. The dev overlay and the console already do that job
    // better. Turn 'development' back on temporarily if you are verifying
    // the Bugsnag wiring itself. 'staging' is gone because nothing sets it.
    enabledReleaseStages: ['test', 'demo', 'production'],
    plugins: [new BugsnagPluginReact(React)],
    onError: (event) => {
      // Drop browser network failures that carry no stack. See
      // lib/bugsnagFilter.ts for exactly what qualifies and why.
      const error = event.errors?.[0];
      if (!error) return true;
      return !isUnactionableNetworkError(
        error.errorClass,
        error.errorMessage,
        error.stacktrace
      );
    },
    metadata: {
      app: {
        name: 'gobo-app-next'
      }
    },
    // Sessions, for the stability score. Unhandled exceptions and promise
    // rejections are captured by default (`enabledErrorTypes` is unset) — see
    // the note below `logError` for why we must not also listen ourselves.
    autoTrackSessions: true,
    collectUserIp: false, // Privacy consideration
    // Enable source map support
    enabledBreadcrumbTypes: ['navigation', 'request', 'process', 'log', 'user', 'state', 'error', 'manual'],
    maxBreadcrumbs: 25,
  })
}

/**
 * Set the current user for Bugsnag error reports
 * Call this when user authenticates or logs out
 */
export function setBugsnagUser(userId: string | null, email?: string | null, name?: string | null) {
  if (typeof window === 'undefined') return;

  if (userId) {
    Bugsnag.setUser(userId, email || undefined, name || undefined);
  } else {
    Bugsnag.setUser(undefined, undefined, undefined);
  }
}

/**
 * Set venue metadata for Bugsnag error reports
 * Call this when venue data is loaded
 */
export function setBugsnagVenueMetadata(venueIds: string[], venueNames?: string[]) {
  if (typeof window === 'undefined') return;

  Bugsnag.addMetadata('venue', {
    venueIds: venueIds,
    venueNames: venueNames || [],
    venueCount: venueIds.length,
  });
}

/**
 * Clear venue metadata (e.g., on logout)
 */
export function clearBugsnagVenueMetadata() {
  if (typeof window === 'undefined') return;

  Bugsnag.clearMetadata('venue');
}

/**
 * Report a caught error to both the console and Bugsnag (client). This is the
 * client-side sibling of `notifyServer` in lib/bugsnag-server.ts — use it in
 * every catch block so failures are never silently swallowed.
 *
 * Optional metadata is attached to the Bugsnag report under "context"; pass
 * only non-PII data (ids, statuses, flags — never raw email / phone / names).
 */
export function logError(error: unknown, metadata?: Record<string, unknown>): void {
  const err = error instanceof Error ? error : new Error(String(error));
  console.error(err, metadata ?? '');
  if (typeof window === 'undefined') return;
  try {
    Bugsnag.notify(err, (event) => {
      if (metadata) event.addMetadata('context', metadata);
    });
  } catch {
    // Last-ditch swallow — never let error reporting itself throw.
  }
}

// ⚠️ There is deliberately NO `unhandledrejection` listener here.
//
// Bugsnag's browser notifier already detects unhandled rejections — we never
// set `enabledErrorTypes`, so the default is on. Adding our own listener on top
// reported every rejection TWICE: once by Bugsnag as unhandled/error, and 40ms
// later by `Bugsnag.notify` as handled/warning, which lands in a second error
// group and makes a genuine crash look like a softer handled one. Two "Failed
// to fetch" groups with ~1,800 occurrences each turned out to be one network
// blip counted twice.

export default Bugsnag

// Create error boundary only on client side
const FallbackErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return React.createElement(React.Fragment, null, children)
}

export const BugsnagErrorBoundary = typeof window !== 'undefined' 
  ? Bugsnag.getPlugin('react')?.createErrorBoundary() || FallbackErrorBoundary
  : FallbackErrorBoundary